import React, { useState, useEffect, useMemo } from 'react';
import { Box, Tabs, Tab, Typography, Skeleton } from '@mui/material';
import { toast } from 'react-toastify';
import * as reportesRecepcionService from '../../services/reportes/reportesRecepcionService';
import { obtenerConfigTurnero } from '../../services/turneroService';
import { listarTiposTurno } from '../../services/tipoTurnoService';
import { usePDFReportes } from '../../components/ReportesPDF';
import { useExcelReportes } from '../../components/ReportesExcel';

import { COLOR, FUENTE, UMBRALES, capacidadDefault } from './reportes/tokens';
import { calcularInsights, calcularAcciones, conversionGlobal } from './reportes/selectors';
import { diasConTurnos } from './reportes/calculosReportes';
import KpiRow from './reportes/KpiRow';
import InsightBar from './reportes/InsightBar';
import AccionesSugeridas from './reportes/AccionesSugeridas';
import EstadosTurnoChart from './reportes/EstadosTurnoChart';
import TablaCanalOrigen from './reportes/TablaCanalOrigen';
import TramitesVolumenConversion from './reportes/TramitesVolumenConversion';
import DemandaPorFranja from './reportes/DemandaPorFranja';
import EsperaEnSala from './reportes/EsperaEnSala';
import TablaBloquesHorarios from './reportes/TablaBloquesHorarios';
import MotivosNoAvance from './reportes/MotivosNoAvance';
import AusentismoPorCanal from './reportes/AusentismoPorCanal';
import EmbudoTurnos from './reportes/EmbudoTurnos';
import TendenciaMensual from './reportes/TendenciaMensual';
import HeatmapDemanda from './reportes/HeatmapDemanda';
import TablaOperadores from './reportes/TablaOperadores';
import CargaVsConversion from './reportes/CargaVsConversion';
import ConversionMensualChart from './reportes/ConversionMensualChart';
import TendenciaPorTipoChart from './reportes/TendenciaPorTipoChart';
import ResultadoAtencionChart from './reportes/ResultadoAtencionChart';
import TurnosPorDiaChart from './reportes/TurnosPorDiaChart';
import TramitesIniciadosPorTipo from './reportes/TramitesIniciadosPorTipo';
import BarraPeriodo from './reportes/BarraPeriodo';
import { usePeriodoReportes } from './reportes/periodoReportes';
import {
    avisoPrimerTurno, calcularPeriodoAnterior, calcularRangoRapido, fechaCorta, fechaISO, periodoAnteriorSinCobertura,
    recortarRango,
} from './reportes/fechasReportes';
import {
    BotonSecundario, EncabezadoReportes, SelectFiltro, TIPO_EXCLUIDO_DE_ESTADISTICAS, TIPOS_TRAMITE_FALLBACK,
} from './reportes/ComponentesReportes';
import NavegacionReportesRecepcion from './reportes/NavegacionReportesRecepcion';
import { AREAS_ATENCION } from '../../constants/areasAtencion';
import { formatearTipoTramite } from '../../constants/tiposTramiteTurno';
import useBreakpoint from '../../hooks/useBreakpoint';
import BottomSheet from '../../components/mobile/BottomSheet';
import BotonFiltros from '../../components/mobile/BotonFiltros';
import ChipsFiltros from '../../components/mobile/ChipsFiltros';
import DatosMaestrosService from '../../services/datosMaestrosService';

const deltaPct = (actual, anterior) => {
    if (actual == null || anterior == null || anterior === 0) return null;
    return ((actual - anterior) / anterior) * 100;
};

// Ventana de la pestaña "Por operador": propia (hoy, 7 días o mes en curso), no la del período de arriba.
// El mes va cortado en hoy: "Por operador" mira turnos finalizados, que nunca pueden ser futuros.
const ventanaOperador = (rango) => (rango === 'hoy'
    ? { desde: fechaISO(new Date()), hasta: fechaISO(new Date()) }
    : recortarRango(calcularRangoRapido(rango === 'semana' ? 'semana' : 'mes'), false));

const TABS = [
    { id: 'general', label: 'General' },
    { id: 'ocupacion', label: 'Ocupación' },
    { id: 'resultados', label: 'Resultados' },
    { id: 'tendencias', label: 'Tendencias' },
    { id: 'operador', label: 'Por operador' },
];

const OPCIONES_EXPORTAR_PDF = [
    { valor: 'todos', label: 'Todos' },
    { valor: 'general', label: 'General' },
    { valor: 'ocupacion', label: 'Ocupación' },
    { valor: 'resultados', label: 'Resultados' },
    { valor: 'tendencias', label: 'Tendencias' },
    { valor: 'operador', label: 'Por Operador' },
];

const ETIQUETA_RANGO_OPERADOR = { hoy: 'Hoy', semana: 'Últimos 7 días', mes: 'Mes en curso' };

/**
 * Estadísticas Pro de Recepción: el tablero completo (ocupación, tendencias, operadores, canal de
 * origen, alertas y acciones sugeridas), que es el módulo que se vendió como "Estadísticas Pro".
 * Antes era la pantalla por defecto de Reportes Recepción; ahora se entra desde la vista simple.
 * Las estadísticas históricas pasaron a su propia vista.
 */
function ReportesRecepcionPro() {
    const periodo = usePeriodoReportes('semana');
    const { desde: filtroFechaInicio, hasta: filtroFechaFin, vacio } = periodo;

    const [datos, setDatos] = useState(null);
    const [datosAnteriores, setDatosAnteriores] = useState(null); // { resumen, conversionPorTipo }
    // true si el período anterior empieza antes del primer turno del sistema: no se compara (ese tramo
    // está vacío, no en cero) y la primera tarjeta lo aclara.
    const [sinComparacion, setSinComparacion] = useState(false);
    const [capacidad, setCapacidad] = useState(capacidadDefault);
    const [loading, setLoading] = useState(true);

    const [filtroTipoTramite, setFiltroTipoTramite] = useState('');
    const [filtroArea, setFiltroArea] = useState('');
    // Canal de origen. Se guarda el ID del catálogo, no el nombre: el reporte agrupa por la
    // FK del turno, y hay turnos con la FK cargada y el texto libre vacío.
    const [filtroReferencia, setFiltroReferencia] = useState('');
    const [referencias, setReferencias] = useState([]);
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    const [rangoOperador, setRangoOperador] = useState('mes');
    const [porOperador, setPorOperador] = useState([]);
    const [loadingOperador, setLoadingOperador] = useState(true);

    const [tiposTramite, setTiposTramite] = useState(TIPOS_TRAMITE_FALLBACK);

    const [activeTab, setActiveTab] = useState('general');
    const [mostrarModalExportarPDF, setMostrarModalExportarPDF] = useState(false);
    const [seccionExportarPDF, setSeccionExportarPDF] = useState('todos');

    useEffect(() => {
        obtenerConfigTurnero()
            .then((cfg) => { if (cfg?.maxTurnosPorHorario) setCapacidad(cfg.maxTurnosPorHorario); })
            .catch(() => {}); // se queda con el default — no bloquea la pantalla por esto
    }, []);

    // Opciones del filtro de tipo de trámite, desde el catálogo editable de Datos Maestros.
    useEffect(() => {
        listarTiposTurno(true)
            .then((lista) => {
                // Solo los tipos que entran en las estadísticas (V237): filtrar por uno excluido daría vacío.
                const codigos = (lista || [])
                    .filter((t) => t?.cuentaEnEstadisticas !== false)
                    .map((t) => t?.codigo)
                    .filter((c) => c && c !== TIPO_EXCLUIDO_DE_ESTADISTICAS);
                if (codigos.length) setTiposTramite(codigos);
            })
            .catch(() => {});
    }, []);

    // Catálogo de referencias (canales de origen) para el filtro. Si falla, el selector queda
    // vacío y el reporte se sigue viendo sin filtrar — no es un dato crítico de la pantalla.
    useEffect(() => {
        DatosMaestrosService.getReferencias()
            .then((res) => {
                const lista = Array.isArray(res?.data) ? res.data : [];
                setReferencias(
                    [...lista].sort((a, b) =>
                        String(a?.nombre || '').localeCompare(String(b?.nombre || ''), 'es'))
                );
            })
            .catch(() => setReferencias([]));
    }, []);

    useEffect(() => {
        // Guarda anti-carrera: si el usuario cambia de filtro mientras esta llamada sigue en
        // vuelo, `vigente` pasa a false en el cleanup y la respuesta que llegue tarde ya no
        // pisa el estado con datos de un filtro que dejó de estar seleccionado.
        let vigente = true;
        const cargar = async () => {
            if (vacio) {
                setDatos({});
                setDatosAnteriores(null);
                setLoading(false);
                return;
            }
            // Desde cuándo hay turnos en el sistema (viene en el resumen): decide si el período anterior es comparable.
            let primerTurno = null;
            try {
                setLoading(true);
                setSinComparacion(false);
                const filtros = {
                    fechaDesde: filtroFechaInicio || null, fechaHasta: filtroFechaFin || null,
                    tipoTramite: filtroTipoTramite || null, area: filtroArea || null,
                    referencia: filtroReferencia || null,
                };
                const data = await reportesRecepcionService.getReportesPrincipales(filtros);
                if (!vigente) return;
                setDatos(data);
                primerTurno = data.resumen?.primerTurno ?? null;
                if (data.huboErrores) {
                    toast.warn('Algunos reportes no se pudieron cargar. Los datos mostrados pueden estar incompletos.');
                }
            } catch (err) {
                if (!vigente) return;
                console.error('Error cargando reportes de recepción:', err);
                toast.error('No se pudieron cargar los reportes. Probá de nuevo o avisá a soporte si persiste.');
                setLoading(false);
                return;
            }
            setLoading(false);

            // Comparación "vs. período anterior" de los KPIs — best-effort: si falla, los
            // KPIs se muestran igual, solo sin el chip de variación.
            const { desde: prevDesde, hasta: prevHasta } = calcularPeriodoAnterior(filtroFechaInicio, filtroFechaFin);
            if (!prevDesde || !prevHasta) {
                setDatosAnteriores(null);
                return;
            }
            // El sistema tiene turnos desde `primerTurno`: si la ventana anterior empieza antes, ese tramo
            // está vacío (no en cero) y la variación saldría inflada ("+78 % de turnos" contra 12 de 20
            // días con datos). No se compara.
            if (periodoAnteriorSinCobertura(prevDesde, primerTurno)) {
                setDatosAnteriores(null);
                setSinComparacion(true);
                return;
            }
            try {
                const filtrosAnterior = { fechaDesde: prevDesde, fechaHasta: prevHasta, tipoTramite: filtroTipoTramite || null, area: filtroArea || null, referencia: filtroReferencia || null };
                const [resumenAnterior, conversionPorTipoAnterior] = await Promise.all([
                    reportesRecepcionService.getResumen(filtrosAnterior),
                    reportesRecepcionService.getConversionPorTipo(filtrosAnterior),
                ]);
                if (!vigente) return;
                setDatosAnteriores({ resumen: resumenAnterior, conversionPorTipo: conversionPorTipoAnterior });
            } catch (err) {
                if (!vigente) return;
                console.error('Error cargando el período anterior para comparar KPIs:', err);
                setDatosAnteriores(null);
            }
        };
        cargar();
        return () => { vigente = false; };
    }, [filtroFechaInicio, filtroFechaFin, vacio, filtroTipoTramite, filtroArea, filtroReferencia]);

    useEffect(() => {
        const cargarPorOperador = async () => {
            try {
                setLoadingOperador(true);
                // El mes va cortado en hoy: "Por operador" mira turnos finalizados, que nunca
                // pueden ser futuros.
                const { desde, hasta } = ventanaOperador(rangoOperador);
                const data = await reportesRecepcionService.getPorOperador({
                    fechaDesde: desde, fechaHasta: hasta,
                    tipoTramite: filtroTipoTramite || null, area: filtroArea || null, referencia: filtroReferencia || null,
                });
                setPorOperador(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Error cargando estadísticas por operador:', err);
                setPorOperador([]);
            } finally {
                setLoadingOperador(false);
            }
        };
        cargarPorOperador();
    }, [rangoOperador, filtroTipoTramite, filtroArea, filtroReferencia]);

    const {
        resumen = null, porTipo = [], porEstado = [], porHorario = [], porMes = [],
        heatmapOcupacion = [], motivosNoAvance = [], tiempoEspera = [], ausentismoPorCanal = [],
        conversionPorTipo = [], conversionMensual = [], tendenciaPorTipo = [], resultadoAtencion = [],
        porDia = [], tramitesIniciadosPorTipo = [],
    } = datos || {};

    const conversionActual = useMemo(() => conversionGlobal(conversionPorTipo), [conversionPorTipo]);
    const conversionAnterior = useMemo(
        () => conversionGlobal(datosAnteriores?.conversionPorTipo),
        [datosAnteriores]
    );

    const kpiItems = useMemo(() => {
        if (!resumen) return [];
        const anterior = datosAnteriores?.resumen;
        const dTurnos = deltaPct(resumen.totalTurnos, anterior?.totalTurnos);
        const dEfectividad = deltaPct(resumen.tasaAsistencia, anterior?.tasaAsistencia);
        const dEspera = deltaPct(resumen.esperaMediaMinutos, anterior?.esperaMediaMinutos);
        const dConversion = deltaPct(conversionActual, conversionAnterior);
        const dCancelados = deltaPct(resumen.turnosCancelados, anterior?.turnosCancelados);
        // Las cancelaciones anteriores a V217 no tienen origen registrado. En vez de repartirlas
        // a ojo se dicen aparte: "3 sin registrar" es información, inventarles un origen no.
        const porBot = resumen.canceladosPorBot ?? 0;
        const manual = resumen.canceladosManual ?? 0;
        const sinRegistro = Math.max(0, (resumen.turnosCancelados ?? 0) - porBot - manual);
        const partes = [];
        if (porBot) partes.push(`${porBot} por el BOT`);
        if (manual) partes.push(`${manual} del estudio`);
        if (sinRegistro) partes.push(`${sinRegistro} sin registrar`);
        const desgloseCancelaciones = partes.length ? partes.join(' · ') : 'ninguna en el período';
        return [
            {
                label: 'Turnos del período', value: (resumen.totalTurnos ?? 0).toLocaleString('es-AR'),
                deltaPct: dTurnos, isGood: dTurnos == null ? null : dTurnos >= 0,
                hint: anterior
                    ? `vs. ${(anterior.totalTurnos ?? 0).toLocaleString('es-AR')} anterior`
                    : (sinComparacion ? 'sin período anterior comparable' : null),
            },
            {
                // Complemento exacto de Ausentismo: los dos se calculan sobre los turnos ya resueltos
                // —vino o no vino, sin cancelados; los sobreturnos cuentan— así que suman 100%. Sin turnos
                // resueltos es "sin datos", no 0 %.
                label: 'Asistencia', value: resumen.tasaAsistencia != null ? Number(resumen.tasaAsistencia).toFixed(1) : '—',
                unit: resumen.tasaAsistencia != null ? '%' : '',
                deltaPct: dEfectividad, isGood: dEfectividad == null ? null : dEfectividad >= 0,
                hint: `de los turnos ya resueltos · meta ${UMBRALES.efectividad.bueno}%`,
            },
            {
                label: 'Espera media', value: resumen.esperaMediaMinutos != null ? Number(resumen.esperaMediaMinutos).toFixed(0) : '—', unit: 'min',
                deltaPct: dEspera, isGood: dEspera == null ? null : dEspera <= 0,
                hint: `promedio por turno, llegada del mismo día · meta ≤${UMBRALES.esperaMin.bueno} min`,
            },
            {
                label: 'Conversión a trámite', value: conversionActual != null ? conversionActual.toFixed(1) : '—', unit: '%',
                deltaPct: dConversion, isGood: dConversion == null ? null : dConversion >= 0,
                hint: `de las atenciones finalizadas · meta ≥${UMBRALES.conversionPct.bueno}%`,
            },
            {
                label: 'Cancelaciones', value: (resumen.turnosCancelados ?? 0).toLocaleString('es-AR'),
                deltaPct: dCancelados, isGood: dCancelados == null ? null : dCancelados <= 0,
                hint: desgloseCancelaciones,
            },
        ];
    }, [resumen, datosAnteriores, sinComparacion, conversionActual, conversionAnterior]);

    // Días con turnos del período: la capacidad es por franja y POR DÍA, y /por-horario suma todos los días
    // del período, así que la ocupación se calcula con el promedio por día.
    const dias = useMemo(() => diasConTurnos(porDia), [porDia]);
    const datosSelectores = useMemo(() => ({
        resumen, porHorario, dias, motivosNoAvance, ausentismoPorCanal, conversionPorTipo, capacidad,
    }), [resumen, porHorario, dias, motivosNoAvance, ausentismoPorCanal, conversionPorTipo, capacidad]);
    const insights = useMemo(() => calcularInsights(datosSelectores), [datosSelectores]);
    const acciones = useMemo(() => calcularAcciones(datosSelectores), [datosSelectores]);

    const conTramiteTotal = useMemo(
        () => conversionPorTipo.reduce((acc, c) => acc + (c.conTramite || 0), 0),
        [conversionPorTipo]
    );
    const avisoPeriodo = avisoPrimerTurno(filtroFechaInicio, resumen?.primerTurno);
    const ventanaOp = ventanaOperador(rangoOperador);

    // Volumen y conversión sobre la misma base (los que se presentaron), con el nombre del tipo
    // formateado. Antes la barra salía de /por-tipo (todos los agendados) y el % de los presentados.
    const volumenConversion = useMemo(
        () => conversionPorTipo.map((c) => ({ ...c, tipoTramite: formatearTipoTramite(c.tipoTramite) })),
        [conversionPorTipo]
    );

    const { generarYDescargar } = usePDFReportes();
    const { generarYDescargar: generarExcelYDescargar } = useExcelReportes();

    // Todo lo que muestra la pantalla, en crudo: PDF y Excel arman sus tablas y gráficos desde acá
    // (ver pages/Recepcion/reportes/exportacionTablas.js), incluidos los filtros aplicados.
    const datosParaExportar = () => ({
        tipo: 'RECEPCION', vista: 'pro', resumen, porTipo, porEstado, porHorario, porMes,
        heatmapOcupacion, motivosNoAvance, tiempoEspera, ausentismoPorCanal, conversionPorTipo, porOperador,
        conversionMensual, tendenciaPorTipo, resultadoAtencion, porDia, tramitesIniciadosPorTipo,
        capacidad,
        historicas: null,
        formatearTipo: formatearTipoTramite,
        filtros: {
            periodo: periodo.etiqueta,
            tipoTramite: filtroTipoTramite ? formatearTipoTramite(filtroTipoTramite) : 'Todos',
            area: filtroArea ? (AREAS_ATENCION.find((a) => a.valor === filtroArea)?.etiqueta || filtroArea) : 'Todas',
            canal: filtroReferencia
                ? (referencias.find((r) => String(r.id) === String(filtroReferencia))?.nombre || 'Canal')
                : 'Todos',
        },
        rangoOperador: ETIQUETA_RANGO_OPERADOR[rangoOperador] || rangoOperador,
    });

    const confirmarExportarPDF = () => {
        try {
            generarYDescargar({ ...datosParaExportar(), seccionExportar: seccionExportarPDF });
            setMostrarModalExportarPDF(false);
        } catch (err) {
            console.error('Error generando el PDF de recepción:', err);
            toast.error('No se pudo generar el PDF. Probá de nuevo o avisá a soporte si persiste.');
        }
    };

    const generarExcel = () => generarExcelYDescargar(datosParaExportar(), 'estadisticas_pro_recepcion');

    const limpiarSeleccion = () => { setFiltroTipoTramite(''); setFiltroArea(''); setFiltroReferencia(''); };
    const chipsFiltros = [
        filtroTipoTramite && { key: 'tipo', label: formatearTipoTramite(filtroTipoTramite), onQuitar: () => setFiltroTipoTramite('') },
        filtroArea && { key: 'area', label: AREAS_ATENCION.find((a) => a.valor === filtroArea)?.etiqueta || 'Área', onQuitar: () => setFiltroArea('') },
        filtroReferencia && {
            key: 'referencia',
            label: referencias.find((r) => String(r.id) === String(filtroReferencia))?.nombre || 'Canal',
            onQuitar: () => setFiltroReferencia(''),
        },
    ].filter(Boolean);

    const selectores = (
        <>
            <SelectFiltro
                label="Tipo de trámite" valor={filtroTipoTramite} onChange={setFiltroTipoTramite}
                opciones={tiposTramite.map((t) => ({ valor: t, etiqueta: formatearTipoTramite(t) }))}
            />
            <SelectFiltro
                label="Área" valor={filtroArea} onChange={setFiltroArea}
                opciones={AREAS_ATENCION.map((a) => ({ valor: a.valor, etiqueta: a.etiqueta }))}
            />
            <SelectFiltro
                label="Canal de origen" valor={filtroReferencia} onChange={setFiltroReferencia}
                opciones={referencias.map((r) => ({ valor: String(r.id), etiqueta: r.nombre }))}
            />
        </>
    );

    return (
        <Box className="mob-fondo-completo area-recepcion" sx={{ background: '#F4F5F7', minHeight: '100vh', margin: { xs: '-0.75rem', md: '-1.5rem' }, padding: { xs: '0.75rem', md: '1.5rem' } }}>
            <EncabezadoReportes
                migas={[{ label: 'Reportes', to: '/reportes' }, { label: 'Recepción', to: '/reportes/recepcion' }, { label: 'Estadísticas Pro' }]}
                titulo="Estadísticas Pro"
                subtitulo={resumen ? `${(resumen.totalTurnos ?? 0).toLocaleString('es-AR')} turnos en el período` : (loading ? 'Cargando…' : 'Sin turnos en el período')}
                acciones={(
                    <>
                        <BotonSecundario onClick={generarExcel}>Exportar Excel</BotonSecundario>
                        <BotonSecundario primario onClick={() => setMostrarModalExportarPDF(true)}>Exportar PDF</BotonSecundario>
                    </>
                )}
            />

            <NavegacionReportesRecepcion activa="pro" />

            <BarraPeriodo periodo={periodo} />

            {avisoPeriodo && (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 12, color: COLOR.grisTexto, marginBottom: '12px' }}>
                    {avisoPeriodo}
                </Typography>
            )}

            {esMobile ? (
                <Box sx={{ marginBottom: '16px' }}>
                    <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                    <ChipsFiltros chips={chipsFiltros} />
                    {sheetFiltrosAbierto && (
                        <BottomSheet
                            titulo="Filtros"
                            onCerrar={() => setSheetFiltrosAbierto(false)}
                            accionPrimaria={chipsFiltros.length > 0 ? { label: 'Quitar filtros', onClick: limpiarSeleccion } : undefined}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>{selectores}</Box>
                        </BottomSheet>
                    )}
                </Box>
            ) : (
                <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '16px' }}>
                    {selectores}
                    {chipsFiltros.length > 0 && (
                        <button
                            type="button"
                            onClick={limpiarSeleccion}
                            style={{ fontFamily: FUENTE, fontSize: 12.5, border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', background: '#fff', cursor: 'pointer', height: 34 }}
                        >
                            Quitar filtros
                        </button>
                    )}
                </Box>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', '& > *': { minWidth: 0, maxWidth: '100%' } }}>
                    <Skeleton variant="rounded" height={96} />
                    <Skeleton variant="rounded" height={80} />
                    <Skeleton variant="rounded" height={280} />
                </Box>
            ) : !resumen ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto }}>
                    No hay datos para el período seleccionado.
                </Typography>
            ) : (
                <>
                    <Box sx={{ marginBottom: '16px' }}>
                        <KpiRow items={kpiItems} />
                    </Box>
                    <Box sx={{ marginBottom: '16px' }}>
                        <InsightBar items={insights} />
                    </Box>

                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        TabIndicatorProps={{ style: { backgroundColor: COLOR.negro, height: 2 } }}
                        sx={{
                            minHeight: 36, marginBottom: '16px', borderBottom: '1px solid #e2e8f0',
                            '& .MuiTab-root': { fontFamily: FUENTE, fontSize: 13.5, fontWeight: 600, textTransform: 'none', minHeight: 36, color: COLOR.grisTexto },
                            '& .Mui-selected': { color: `${COLOR.negro} !important` },
                        }}
                    >
                        {TABS.map((t) => <Tab key={t.id} value={t.id} label={t.label} />)}
                    </Tabs>

                    {activeTab === 'general' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', '& > *': { minWidth: 0, maxWidth: '100%' } }}>
                            <EmbudoTurnos
                                agendado={resumen.totalTurnos ?? 0}
                                presentaron={resumen.turnosAtendidos ?? 0}
                                conTramite={conTramiteTotal}
                                noAsistio={resumen.turnosNoAsistio ?? 0}
                                cancelados={resumen.turnosCancelados ?? 0}
                            />
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: '16px' }}>
                                <EstadosTurnoChart data={porEstado} asistencia={resumen.tasaAsistencia} />
                                <TablaCanalOrigen data={ausentismoPorCanal} />
                            </Box>
                            <TramitesVolumenConversion data={volumenConversion} />
                            <TramitesIniciadosPorTipo data={tramitesIniciadosPorTipo} />
                            <AccionesSugeridas items={acciones} />
                        </Box>
                    )}

                    {activeTab === 'ocupacion' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', '& > *': { minWidth: 0, maxWidth: '100%' } }}>
                            <DemandaPorFranja data={porHorario} capacidad={capacidad} dias={dias} />
                            <TablaBloquesHorarios porHorario={porHorario} tiempoEspera={tiempoEspera} capacidad={capacidad} dias={dias} />
                            <EsperaEnSala data={tiempoEspera} esperaGeneral={resumen.esperaMediaMinutos} />
                            <HeatmapDemanda data={heatmapOcupacion} />
                        </Box>
                    )}

                    {activeTab === 'resultados' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', '& > *': { minWidth: 0, maxWidth: '100%' } }}>
                            <ResultadoAtencionChart data={resultadoAtencion} />
                            <MotivosNoAvance data={motivosNoAvance} />
                            <AusentismoPorCanal
                                data={ausentismoPorCanal}
                                promedioEstudio={resumen.tasaAusentismo != null ? Number(resumen.tasaAusentismo) : null}
                            />
                        </Box>
                    )}

                    {activeTab === 'tendencias' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', '& > *': { minWidth: 0, maxWidth: '100%' } }}>
                            <TendenciaMensual data={porMes} />
                            <ConversionMensualChart data={conversionMensual} />
                            <TendenciaPorTipoChart data={tendenciaPorTipo} tipoTramiteActivo={filtroTipoTramite || null} />
                            <TurnosPorDiaChart data={porDia} />
                        </Box>
                    )}

                    {activeTab === 'operador' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', '& > *': { minWidth: 0, maxWidth: '100%' } }}>
                            <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                {['hoy', 'semana', 'mes'].map((r) => (
                                    <button
                                        key={r} type="button" onClick={() => setRangoOperador(r)}
                                        style={{
                                            fontFamily: FUENTE, fontSize: 12.5, fontWeight: 600, border: '1px solid #e2e8f0',
                                            borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                                            background: rangoOperador === r ? COLOR.negro : '#fff',
                                            color: rangoOperador === r ? '#fff' : COLOR.grisTexto,
                                        }}
                                    >
                                        {ETIQUETA_RANGO_OPERADOR[r]}
                                    </button>
                                ))}
                            </Box>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 12, color: COLOR.grisTexto }}>
                                Período de esta pestaña: del {fechaCorta(ventanaOp.desde)} al {fechaCorta(ventanaOp.hasta)}. Es propio y no
                                cambia con el de arriba. Cuenta solo las atenciones finalizadas.
                            </Typography>
                            {loadingOperador ? (
                                <Skeleton variant="rounded" height={220} />
                            ) : porOperador.length === 0 ? (
                                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto }}>No hay turnos finalizados en el período seleccionado.</Typography>
                            ) : (
                                <>
                                    <TablaOperadores data={porOperador} />
                                    <CargaVsConversion data={porOperador} />
                                </>
                            )}
                        </Box>
                    )}
                </>
            )}

            {mostrarModalExportarPDF && (
                <Box
                    sx={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setMostrarModalExportarPDF(false)}
                >
                    <Box
                        sx={{ background: '#fff', borderRadius: '16px', width: 380, maxWidth: '95vw', overflow: 'hidden' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Box sx={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0' }}>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 15, fontWeight: 700, color: COLOR.negro }}>Exportar PDF</Typography>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 12, color: COLOR.grisTexto }}>Elegí qué reporte querés exportar</Typography>
                        </Box>
                        <Box sx={{ padding: '18px 24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {OPCIONES_EXPORTAR_PDF.map((op) => (
                                <label key={op.valor} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: FUENTE, fontSize: 13, color: COLOR.negro, cursor: 'pointer' }}>
                                    <input type="radio" name="seccionExportarPDF" checked={seccionExportarPDF === op.valor} onChange={() => setSeccionExportarPDF(op.valor)} />
                                    {op.label}
                                </label>
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '14px 24px', borderTop: '1px solid #e2e8f0' }}>
                            <button type="button" onClick={() => setMostrarModalExportarPDF(false)} style={{ fontFamily: FUENTE, fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 14px', background: '#fff', cursor: 'pointer' }}>
                                Cancelar
                            </button>
                            <button type="button" onClick={confirmarExportarPDF} style={{ fontFamily: FUENTE, fontSize: 13, border: 'none', borderRadius: 8, padding: '7px 14px', background: COLOR.azul, color: '#fff', cursor: 'pointer' }}>
                                Exportar
                            </button>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
}

export default ReportesRecepcionPro;
